import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useFetch } from '@/modules/core/composables/useFetch'
import { rails_url } from '@/modules/shared/utils/rails'
import { useRoute } from 'vue-router'

///////////////////////////////////////////////////////////////////////////////
// Types
///////////////////////////////////////////////////////////////////////////////

type AccessToken = string

type MagicLinkToken = string

export type Role = 'site_admin' | 'group_admin' | 'investor' | 'guest' | 'company_user'

type Slug = string

type Status = 'active' | 'inactive'

///////////////////////////////////////////////////////////////////////////////
// Store
///////////////////////////////////////////////////////////////////////////////

type CurrentUser = {
  id: string | number
  email: string
  groups: any[]
  intercom_hash: string
  investor_id: string | number
  initials: string
  managed_investor_set_ids: string[]
  managed_individual_ids: string[]
  name: string
  profile_picture?: string
  role: Role
  status: Status
  two_factor_enabled_at?: string
  two_factor_requested_at: string
}

const initialState: CurrentUser = {
  id: null,
  email: null,
  groups: [],
  intercom_hash: null,
  investor_id: null,
  name: null,
  role: null,
  two_factor_requested_at: null,
}

export const useAuthStore = defineStore('authStore', () => {
  const route = useRoute()
  const access_token = useStorage<AccessToken>('access_token', null)
  const current_user = useStorage<CurrentUser>('current_user', { ...initialState })
  const original_user = useStorage<CurrentUser>('original_user', { ...initialState })
  const slug = useStorage<Slug>('slug', null)

  const has_current_user = computed(() => !!current_user.value.email)
  const is_authenticated = computed(() => !!access_token.value)
  const isInvestor = computed(() => current_user.value.role === 'investor')
  const is_site_admin = computed(() => current_user.value.role === 'site_admin')
  const is_group_admin = computed(() => current_user.value.role === 'group_admin')
  const is_site_or_group_admin = computed(
    () => current_user.value.role === 'site_admin' || current_user.value.role === 'group_admin',
  )
  const is_company_user = computed(() => current_user.value.role === 'company_user')
  const logged_in_as = computed(
    () =>
      is_authenticated.value === true &&
      original_user?.value?.id &&
      current_user.value.investor_id != original_user.value.investor_id,
  )

  const logout = async () => {
    if (access_token.value) {
      try {
        await useFetch('/v4/auth/logout').post()
      } catch {
        // Swallow — we're clearing state regardless
      }
    }

    reset()
  }

  const validateToken = async () => {
    try {
      await useFetch('/v4/auth/token/validate').get()
      return true
    } catch {
      return false
    }
  }

  const reset = () => {
    access_token.value = null
    current_user.value = { ...initialState }
    original_user.value = { ...initialState }
    slug.value = null
  }

  // Discriminated result so the router guard can tell "the slug in the URL
  // doesn't match any group" (→ /not-found) apart from "session is dead"
  // (→ logout). Without this distinction the guard logs the user out for
  // both, which is what causes the typo'd-subdomain logout bug.
  type RefreshResult = { ok: true } | { ok: false; reason: 'slug_not_found' | 'session_invalid' }

  const refresh = async (token?: AccessToken, route?: any): Promise<RefreshResult> => {
    access_token.value = token || access_token.value
    try {
      const result = await useFetch(`/v4/${route.params.slug}/auth/current`).get<{ data: CurrentUser }>()
      current_user.value = result.data
      return { ok: true }
    } catch (e) {
      console.error(e)
      current_user.value = { ...initialState }
      const reason = e.message === 'not_found' ? 'slug_not_found' : 'session_invalid'
      return { ok: false, reason }
    }
  }

  const requestVerificationCode = async (channel: 'sms' | 'email') => {
    return useFetch('/v4/auth/send-verification-code').post({ channel })
  }

  const viewAs = async (user_id) => {
    if (!user_id) return
    original_user.value = current_user.value
    window.location.href = `${rails_url(route.params.slug)}/login-as/${user_id}`
  }

  const requestEmailVerification = async (payload: { email: string; registration_type: 'company' | 'investor' }) => {
    try {
      const result = await useFetch(`/v4/${route.params.slug}/auth/request-email-verification`).post(payload)
      return { data: result?.data, error: null }
    } catch (e) {
      console.error(e.message)
      return { data: null, error: e.message }
    }
  }

  return {
    access_token,
    current_user,
    logged_in_as,
    original_user,
    slug,

    has_current_user,
    is_authenticated,
    isInvestor,
    is_company_user,
    is_site_admin,
    is_site_or_group_admin,
    is_group_admin,

    logout,
    refresh,
    requestVerificationCode,
    reset,
    validateToken,
    viewAs,

    requestEmailVerification,
  }
})
